import type { Billing, PricePlan, PricedCounty, PricingCatalogue } from "@/api/models";

/** The most counties one plan holds, as the API's `MAX_PLAN_COUNTIES`. */
export const MAX_COUNTIES = 3;

export interface Chosen {
  /** The agent's own county first, then up to two more of its area. */
  counties: PricedCounty[];
  plan: PricePlan;
  billing: Billing;
}

function findCounty(catalogue: PricingCatalogue, name: string): PricedCounty | undefined {
  return catalogue.counties.find((item) => item.slug === name || item.key === name);
}

/** The plan these counties make, as the API computes it (`Catalogue.plan_for`): up to three
 * counties of one area, on the smallest plan that holds every one of them; null when they do
 * not fit one plan. The agent never chooses a plan, so a price always follows from the place. */
export function planFor(catalogue: PricingCatalogue, counties: PricedCounty[]): PricePlan | null {
  const [home] = counties;
  if (!home || counties.length > MAX_COUNTIES || counties.some((item) => item.area !== home.area)) return null;
  const least = Math.max(...counties.map((item) => catalogue.plans.find((plan) => plan.key === item.plan)?.counties ?? 1));
  return (
    [...catalogue.plans]
      .sort((a, b) => a.counties - b.counties)
      .find((plan) => plan.counties >= counties.length && plan.counties >= least) ?? null
  );
}

/** A choice from county names, the agent's own first; repeats and unknown names drop out, and
 * counties that do not fit one plan keep the agent's own county alone, as the API does. */
export function choose(catalogue: PricingCatalogue | undefined, names: string[], billing: string | null): Chosen | null {
  if (!catalogue) return null;
  const counties: PricedCounty[] = [];
  for (const name of names) {
    const found = findCounty(catalogue, name);
    if (found && !counties.includes(found)) counties.push(found);
  }
  const kept = planFor(catalogue, counties) ? counties : counties.slice(0, 1);
  const plan = planFor(catalogue, kept);
  if (!plan) return null;
  return { counties: kept, plan, billing: billing === "year" ? "year" : "month" };
}

/** The other counties of a county's area, which one plan can hold with it. */
export function areaCounties(catalogue: PricingCatalogue, home: PricedCounty): PricedCounty[] {
  return catalogue.counties.filter((item) => item.area === home.area && item.slug !== home.slug);
}

/** The area's name as the agent reads it: "Houston". */
export function areaName(catalogue: PricingCatalogue, home: PricedCounty): string {
  return catalogue.areas.find((area) => area.key === home.area)?.name ?? home.area;
}

/** What a county starts with: a county that already costs the larger plan comes with the rest
 * of its area when that fits, since the price is the same; any other county starts alone. */
export function startingCounties(catalogue: PricingCatalogue, home: PricedCounty): PricedCounty[] {
  const others = areaCounties(catalogue, home).filter((item) => !item.opening_soon);
  const alone = planFor(catalogue, [home]);
  const together = [home, ...others];
  const whole = planFor(catalogue, together);
  return alone && whole && whole.key === alone.key ? together : [home];
}

/** "$99 a month", or "$990 a year". */
export function priceWords(plan: PricePlan, billing: Billing): string {
  return billing === "year" ? `$${plan.yearly.toLocaleString("en-US")} a year` : `$${plan.monthly} a month`;
}

/** "Harris County, TX", or "Harris, Montgomery and Fort Bend counties, TX". */
export function countiesWords(counties: PricedCounty[]): string {
  const [first] = counties;
  if (!first) return "";
  if (counties.length === 1) return first.display;
  const names = counties.map((item) => item.key.split(", ")[0]);
  const state = first.key.split(", ")[1];
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)} counties, ${state}`;
}
